/**
 * DataTable Component
 *
 * Generic table for displaying district/category data with sorting
 */

import type React from 'react'

export interface Column<T> {
  /** Column header */
  header: string
  /** Accessor function or key */
  accessor: keyof T | ((row: T) => React.ReactNode)
  /** Column width class (e.g., 'w-1/4') */
  width?: string
  /** Text alignment */
  align?: 'left' | 'center' | 'right'
  /** Custom cell renderer */
  render?: (value: unknown, row: T, index?: number) => React.ReactNode
}

interface DataTableProps<T> {
  /** Table columns */
  columns: Column<T>[]
  /** Table data */
  data: T[]
  /** Loading state */
  isLoading?: boolean
  /** Error state */
  error?: Error | null
  /** Custom className */
  className?: string
  /** Empty state message */
  emptyMessage?: string
}

export function DataTable<T = Record<string, unknown>>({
  columns,
  data,
  isLoading = false,
  error = null,
  className = '',
  emptyMessage = 'ไม่พบข้อมูล',
}: DataTableProps<T>) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-red-600">
        <div className="flex flex-col items-center gap-2">
          <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm font-medium">เกิดข้อผิดพลาด</p>
          <p className="text-xs text-gray-500">{error.message}</p>
        </div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <p className="text-sm">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                scope="col"
                className={`px-4 py-3 text-xs font-medium text-gray-700 uppercase tracking-wider ${
                  column.width || ''
                } ${
                  column.align === 'right'
                    ? 'text-right'
                    : column.align === 'center'
                    ? 'text-center'
                    : 'text-left'
                }`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} className="hover:bg-gray-50 transition-colors">
              {columns.map((column, colIndex) => {
                const value =
                  typeof column.accessor === 'function'
                    ? column.accessor(row)
                    : row[column.accessor]

                const cellContent = column.render
                  ? column.render(value, row, rowIndex)
                  : value

                return (
                  <td
                    key={colIndex}
                    className={`px-4 py-3 text-sm text-gray-900 ${
                      column.align === 'right'
                        ? 'text-right'
                        : column.align === 'center'
                        ? 'text-center'
                        : 'text-left'
                    }`}
                  >
                    {cellContent as React.ReactNode}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
